import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Add,
  FilterList,
  Refresh,
  Download,
  MoreVert
} from '@mui/icons-material';

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
  actions?: {
    primary?: {
      label: string;
      icon?: React.ReactNode;
      onClick: () => void;
      variant?: 'contained' | 'outlined' | 'text';
      color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
    };
    secondary?: {
      label: string;
      icon?: React.ReactNode;
      onClick: () => void;
      variant?: 'contained' | 'outlined' | 'text';
    }[];
  };
  filters?: {
    label: string;
    count?: number;
    active?: boolean;
    onClick: () => void;
  }[];
  badge?: {
    count: number;
    color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  };
  onRefresh?: () => void;
  loading?: boolean;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  subtitle,
  actions,
  filters,
  badge,
  onRefresh,
  loading = false
}) => {
  return (
    <Box sx={{ mb: 3 }}>
      {/* Header cu titlu si actiuni */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={subtitle ? 0.5 : 0}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {title}
            </Typography>
            {badge && badge.count > 0 && (
              <Chip
                label={badge.count}
                size="small"
                color={badge.color || 'primary'}
                sx={{ height: 24, fontWeight: 600 }}
              />
            )}
          </Box>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {/* Actiuni secundare */}
          {actions?.secondary?.map((action, index) => (
            <Button
              key={index}
              variant={action.variant || 'outlined'}
              size="small"
              startIcon={action.icon}
              onClick={action.onClick}
              sx={{ textTransform: 'none' }}
            >
              {action.label}
            </Button>
          ))}

          {/* Refresh button */}
          {onRefresh && (
            <Tooltip title="Actualizeaza">
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={loading}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          )}

          {/* Actiune primara */}
          {actions?.primary && (
            <Button
              variant={actions.primary.variant || 'contained'}
              color={actions.primary.color || 'primary'}
              startIcon={actions.primary.icon || <Add />}
              onClick={actions.primary.onClick}
              sx={{ 
                textTransform: 'none',
                fontWeight: 500,
                px: 2
              }}
            >
              {actions.primary.label}
            </Button>
          )}
        </Box>
      </Box>

      {/* Filtre */}
      {filters && filters.length > 0 && (
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {filters.map((filter, index) => (
            <Chip
              key={index}
              label={`${filter.label}${filter.count ? ` (${filter.count})` : ''}`}
              variant={filter.active ? 'filled' : 'outlined'}
              color={filter.active ? 'primary' : 'default'}
              onClick={filter.onClick}
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: filter.active 
                    ? 'primary.dark' 
                    : 'action.hover'
                }
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SectionHeader;
